/* eslint-disable react/require-default-props */
import { useMutation } from '@apollo/client';
import { ColumnsType } from 'antd/lib/table';
import { ERRORS } from 'constants/notify';
import usStates from 'constants/usStates';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import { useRoutesDisplayValues } from 'hooks/useRoutesDisplayValues';
import { get } from 'lodash';
import React, { useMemo, useState } from 'react';
import ROUTES from 'routes/routeConstants';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_COMPANY_LOCATION } from 'services/graphql/queries/location.query';
import { ILocation } from 'types/location';
import { CompanyRoleEnum } from 'types/user';
import { defaultNotification, formatPhoneNumber } from 'utils';

// import SetMainLocationButton from './SetMainLocationButton';
import { ILocationTableMapper } from './types';

export const useManageLocationsData = () => {
  const routeDisplayValue = useRoutesDisplayValues()[ROUTES.COMPANY_LOCATION];
  const user = useCurrentLoggedInUser(); // test nha
  const { companyRole, id: userId } = user;
  const isCompanyAdmin = companyRole === CompanyRoleEnum.ADMIN;
  const [locationModalVisible, setLocationModalVisible] = useState(false);

  // Location tables related props ==============

  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER);

  const updateMainLocation = async (location: ILocation) => {
    try {
      await updateUser({
        variables: {
          filter: { id: userId },
          updatedDoc: {
            mainLocation: location.id,
          },
        },
      });
      // const updatedData = updatedDataFromMutation?.data.updateUserEntity;
      // if (updatedData) dispatch(UserAction.fetchUserSuccess({ mainLocation: updatedData.mainLocation }));
      defaultNotification({ success: 'Update Main Location Successfully!' });
      // refetch();
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };

  const locationListColumn: ColumnsType<ILocationTableMapper> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        render: (name: string) => <span className='gx-font-weight-medium'>{name}</span>,
      },
      {
        title: 'Address',
        dataIndex: 'fullAddress',
      },
      {
        title: 'City',
        dataIndex: 'city',
      },
      {
        title: 'State',
        dataIndex: 'state',
      },

      {
        title: 'Phone',
        dataIndex: 'number',
        sorter: true,
        render: (number: string) => (
          <>{number ? <div>{formatPhoneNumber(number)}</div> : <div className='text-lg'>--</div>}</>
        ),
      },
      // {
      //   title: '',
      //   dataIndex: 'location',
      //   render: (location: ILocation) => (
      //     <div className='flex justify-end'>
      //       <SetMainLocationButton
      //         user={user}
      //         location={location}
      //         loading={updateUserLoading}
      //         updateMainLocation={updateMainLocation}
      //       />
      //     </div>
      //   ),
      // },
    ],
    [updateUserLoading, user],
  );

  const companyAdminDataMapper = (location: ILocation) => ({
    key: location.id,
    location,
    address: location.address?.address1,
    name: location.name,
    number: location.number,
    fullAddress: location.address?.address1 || '',
    ...location.address,
    state: get(usStates, location.address?.state || ''),
  });

  const companyAdminLocationTableProps = useReactiveQueryTable<any, ILocationTableMapper>({
    graphQLQuery: GET_COMPANY_LOCATION,
    columns: locationListColumn,
    queryProps: {
      skip: !isCompanyAdmin,
    },
    dataMapper: companyAdminDataMapper,
  });

  const regularClientLocationDataSource = useMemo(() => {
    const loggedInUserLocations = user?.usersLocations?.map((uL) => uL.location) || [];
    return loggedInUserLocations.map((location) => ({
      key: location.id,
      location,
      fullAddress: location.address?.address1 || '',
      address: location.address?.address1,
      name: location.name,
      number: location.number,
      ...location.address,
    }));
  }, [user]);

  const regularClientLocationTableProps = {
    refetch: () => {},
    dataSource: regularClientLocationDataSource,
    columns: locationListColumn,
    // loading: false,
  };
  const locationTableProps = isCompanyAdmin ? companyAdminLocationTableProps : regularClientLocationTableProps;

  return {
    locationTableProps,
    routeDisplayValue,
    locationModalVisible,
    setLocationModalVisible,
    isCompanyAdmin,
    updateMainLocation,
    updateUserLoading,
  };
};
